<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import inboxMixin from 'shared/mixins/inboxMixin';
import SettingsFieldSection from 'dashboard/components-next/Settings/SettingsFieldSection.vue';
import LoadingState from 'dashboard/components/widgets/LoadingState.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import SelectInput from 'dashboard/components-next/select/Select.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import TextArea from 'next/textarea/TextArea.vue';

export default {
  components: {
    LoadingState,
    SettingsFieldSection,
    NextButton,
    SelectInput,
    Switch,
    TextArea,
  },
  mixins: [inboxMixin],
  props: {
    inbox: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      selectedAgentBotId: null,
      proactiveGreetingEnabled: false,
      proactiveGreetingMessage: '',
      isSavingProactiveGreeting: false,
    };
  },
  computed: {
    ...mapGetters({
      agentBots: 'agentBots/getBots',
      uiFlags: 'agentBots/getUIFlags',
    }),
    currentInboxId() {
      return this.inbox?.id || this.$route.params.inboxId;
    },
    activeAgentBot() {
      return this.$store.getters['agentBots/getActiveAgentBot'](
        this.currentInboxId
      );
    },
    showProactiveGreeting() {
      return this.isAWebWidgetInbox && Boolean(this.selectedAgentBotId);
    },
    channelGreetingConflict() {
      return (
        this.showProactiveGreeting &&
        this.proactiveGreetingEnabled &&
        this.inbox.greeting_enabled
      );
    },
  },
  watch: {
    activeAgentBot() {
      this.selectedAgentBotId = this.activeAgentBot?.id || null;
    },
    inbox: {
      immediate: true,
      handler() {
        this.syncProactiveGreetingFromInbox();
      },
    },
  },
  mounted() {
    this.fetchBotData();
    this.syncProactiveGreetingFromInbox();
  },

  methods: {
    fetchBotData() {
      this.$store.dispatch('agentBots/get');
      this.$store.dispatch('agentBots/fetchAgentBotInbox', this.currentInboxId);
    },
    syncProactiveGreetingFromInbox() {
      this.proactiveGreetingEnabled = Boolean(
        this.inbox.widget_proactive_greeting_enabled
      );
      this.proactiveGreetingMessage =
        this.inbox.widget_proactive_greeting_message || '';
    },
    async updateActiveAgentBot() {
      try {
        await this.$store.dispatch('agentBots/setAgentBotInbox', {
          inboxId: this.inbox.id,
          // Added this to make sure that empty values are not sent to the API
          botId: this.selectedAgentBotId ? this.selectedAgentBotId : undefined,
        });
        useAlert(this.$t('AGENT_BOTS.BOT_CONFIGURATION.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(this.$t('AGENT_BOTS.BOT_CONFIGURATION.ERROR_MESSAGE'));
      }
    },
    async disconnectBot() {
      try {
        await this.$store.dispatch('agentBots/disconnectBot', {
          inboxId: this.inbox.id,
        });
        useAlert(
          this.$t('AGENT_BOTS.BOT_CONFIGURATION.DISCONNECTED_SUCCESS_MESSAGE')
        );
      } catch (error) {
        useAlert(
          error?.message ||
            this.$t('AGENT_BOTS.BOT_CONFIGURATION.DISCONNECTED_ERROR_MESSAGE')
        );
      }
    },
    async saveProactiveGreeting() {
      this.isSavingProactiveGreeting = true;
      try {
        await this.$store.dispatch('inboxes/updateInbox', {
          id: this.inbox.id,
          formData: false,
          channel: {
            widget_proactive_greeting_enabled: this.proactiveGreetingEnabled,
            widget_proactive_greeting_message: this.proactiveGreetingMessage,
          },
        });
        useAlert(this.$t('AGENT_BOTS.BOT_CONFIGURATION.PROACTIVE_GREETING.SAVED'));
      } catch (error) {
        useAlert(this.$t('AGENT_BOTS.BOT_CONFIGURATION.PROACTIVE_GREETING.SAVE_FAILED'));
      } finally {
        this.isSavingProactiveGreeting = false;
      }
    },
  },
};
</script>

<template>
  <div class="mx-6 max-w-4xl">
    <LoadingState v-if="uiFlags.isFetching || uiFlags.isFetchingAgentBot" />
    <form v-else @submit.prevent="updateActiveAgentBot">
      <SettingsFieldSection
        :label="$t('AGENT_BOTS.BOT_CONFIGURATION.TITLE')"
        :help-text="$t('AGENT_BOTS.BOT_CONFIGURATION.DESC')"
        class="[&>div]:!items-start"
      >
        <SelectInput
          v-model="selectedAgentBotId"
          :placeholder="$t('AGENT_BOTS.BOT_CONFIGURATION.SELECT_PLACEHOLDER')"
          :options="agentBots.map(bot => ({ value: bot.id, label: bot.name }))"
        />
        <template #extra>
          <div class="grid grid-cols-1 lg:grid-cols-8 mt-3">
            <div class="col-span-1 lg:col-span-2 invisible" />
            <div class="col-span-1 lg:col-span-6 flex gap-2 mx-1">
              <NextButton
                type="submit"
                :label="$t('AGENT_BOTS.BOT_CONFIGURATION.SUBMIT')"
                :is-loading="uiFlags.isSettingAgentBot"
              />
              <NextButton
                type="button"
                :disabled="!selectedAgentBotId"
                :is-loading="uiFlags.isDisconnecting"
                faded
                ruby
                @click="disconnectBot"
              >
                {{ $t('AGENT_BOTS.BOT_CONFIGURATION.DISCONNECT') }}
              </NextButton>
            </div>
          </div>
        </template>
      </SettingsFieldSection>
    </form>

    <form
      v-if="showProactiveGreeting"
      class="mt-8"
      @submit.prevent="saveProactiveGreeting"
    >
      <SettingsFieldSection
        :label="$t('AGENT_BOTS.BOT_CONFIGURATION.PROACTIVE_GREETING.TITLE')"
        :help-text="$t('AGENT_BOTS.BOT_CONFIGURATION.PROACTIVE_GREETING.HINT')"
        class="[&>div]:!items-start"
      >
        <div class="flex flex-col gap-4 w-full">
          <div class="flex items-center gap-3">
            <Switch v-model="proactiveGreetingEnabled" />
            <span class="text-sm text-n-slate-11">
              {{ $t('AGENT_BOTS.BOT_CONFIGURATION.PROACTIVE_GREETING.ENABLED') }}
            </span>
          </div>
          <TextArea
            v-model="proactiveGreetingMessage"
            :placeholder="
              $t('AGENT_BOTS.BOT_CONFIGURATION.PROACTIVE_GREETING.PLACEHOLDER')
            "
            :rows="3"
            :disabled="!proactiveGreetingEnabled"
          />
          <p
            v-if="channelGreetingConflict"
            class="text-xs text-n-ruby-11"
          >
            {{ $t('AGENT_BOTS.BOT_CONFIGURATION.PROACTIVE_GREETING.CHANNEL_GREETING_WARNING') }}
          </p>
        </div>
        <template #extra>
          <div class="grid grid-cols-1 lg:grid-cols-8 mt-3">
            <div class="col-span-1 lg:col-span-2 invisible" />
            <div class="col-span-1 lg:col-span-6 mx-1">
              <NextButton
                type="submit"
                :label="$t('AGENT_BOTS.BOT_CONFIGURATION.PROACTIVE_GREETING.SAVE')"
                :is-loading="isSavingProactiveGreeting"
              />
            </div>
          </div>
        </template>
      </SettingsFieldSection>
    </form>
  </div>
</template>
